import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export default function ManageTutorials() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showTutorialForm, setShowTutorialForm] = useState(false);
  const [tutorialForm, setTutorialForm] = useState({ title: '', duration: '', url: '' });
  const [savingTutorialId, setSavingTutorialId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  
  const initialTutorials = [
    { id: 1, title: 'Tutorial 1', duration: '20 mins', url: 'https://drive.google.com/file/d/16NYN4YGqodC1-A-yE1I-cj7kQnyWF1gL/view?usp=sharing', status: 'released' },
    { id: 2, title: 'Tutorial 2', duration: '22 mins', url: 'https://drive.google.com/file/d/1-D-zdDEF9_F4--iQCt9alTNoK99CuMSP/view?usp=sharing', status: 'released' },
    { id: 3, title: 'Tutorial 3', duration: '25 mins', url: 'https://drive.google.com/file/d/1McZ4oy8sUuHhhACqmQBXS6zCxTUq4Tjd/view?usp=sharing', status: 'closed' },
    { id: 4, title: 'Tutorial 4', duration: '21 mins', url: 'https://drive.google.com/file/d/1s4dE0FZlmCDEaQQgh0HkLDZPoA6Tmfd-/view?usp=sharing', status: 'closed' },
    { id: 5, title: 'Tutorial 5', duration: '23 mins', url: 'https://drive.google.com/file/d/11zRMD-QXFGMIe5G8HLiIApLpIFekTjRP/view?usp=sharing', status: 'closed' },
    { id: 6, title: 'Tutorial 6', duration: '24 mins', url: 'https://drive.google.com/file/d/1PoUO-3zJ5E0nBg9kGP1gONWgK-DkWDec/view?usp=sharing', status: 'closed' },
    { id: 7, title: 'Tutorial 7', duration: '20 mins', url: 'https://drive.google.com/file/d/1FZEWobP-fsPOb1bKSi73ETHa7iQTXHUh/view?usp=sharing', status: 'closed' },
    { id: 8, title: 'Tutorial 8', duration: '26 mins', url: 'https://drive.google.com/file/d/1JKXp-A5MO2TRdHYpFCGySydv7RicEsg0/view?usp=sharing', status: 'closed' },
    { id: 9, title: 'Tutorial 9', duration: '28 mins', url: 'https://drive.google.com/file/d/1l2KxiGWf7MOQnfSaUXEHiGqT89jTRCZs/view?usp=sharing', status: 'closed' }
  ];
  
  const [tutorials, setTutorials] = useState(initialTutorials);

  // Load tutorial statuses from Firestore on mount
  useEffect(() => {
    const loadTutorialStatuses = async () => {
      if (!user || !user.id) {
        console.log('User not available yet');
        return;
      }
      
      try {
        console.log('Loading tutorial statuses for user:', user.id);
        const statusRef = doc(db, 'instructors', user.id, 'tutorialStatuses', 'statuses');
        const statusDoc = await getDoc(statusRef);
        
        if (statusDoc.exists()) {
          const savedStatuses = statusDoc.data();
          console.log('Found saved statuses:', savedStatuses);
          // Update tutorials with saved statuses from Firestore
          setTutorials(prevTutorials => 
            prevTutorials.map(tutorial => ({
              ...tutorial,
              status: savedStatuses[`tutorial_${tutorial.id}`] || tutorial.status
            }))
          );
        } else {
          console.log('No saved statuses found in Firestore');
        }
      } catch (error) {
        console.error('Error loading tutorial statuses from Firestore:', error);
      }
    };

    loadTutorialStatuses();
  }, [user]);

  const handleAddTutorial = (e) => {
    e.preventDefault();
    if (tutorialForm.title && tutorialForm.duration && tutorialForm.url) {
      setTutorials([...tutorials, {
        id: Math.max(...tutorials.map(t => t.id), 0) + 1,
        ...tutorialForm,
        status: 'closed'
      }]);
      setTutorialForm({ title: '', duration: '', url: '' });
      setShowTutorialForm(false);
    }
  };

  const toggleTutorialStatus = async (id) => {
    setSavingTutorialId(id);
    const updatedTutorials = tutorials.map(t => t.id === id ? { ...t, status: t.status === 'released' ? 'closed' : 'released' } : t);
    setTutorials(updatedTutorials);
    const tutorial = updatedTutorials.find(t => t.id === id);
    
    console.log('Toggling tutorial:', id, 'New status:', tutorial.status);
    console.log('User ID:', user?.id);

    // Save to Firestore
    if (user && user.id) {
      try {
        // Ensure the parent instructor document exists - this is critical for student queries
        const instructorRef = doc(db, 'instructors', user.id);
        await setDoc(instructorRef, { createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, { merge: true });
        
        const statusRef = doc(db, 'instructors', user.id, 'tutorialStatuses', 'statuses');
        console.log('Saving to Firestore path:', `instructors/${user.id}/tutorialStatuses/statuses`);
        
        // Get existing statuses first
        const statusDoc = await getDoc(statusRef);
        const existingStatuses = statusDoc.exists() ? statusDoc.data() : {};
        
        const updateData = {
          ...existingStatuses,
          [`tutorial_${id}`]: tutorial.status,
          lastUpdated: new Date().toISOString()
        };
        
        console.log('Saving data:', updateData);
        
        // Update with new status
        await setDoc(statusRef, updateData);
        console.log('Successfully saved tutorial status:', tutorial.status);
        
        // Show success message
        setStatusMessage({
          type: 'success',
          title: `Tutorial ${tutorial.status === 'released' ? 'Released' : 'Closed'}`,
          message: `"${tutorial.title}" is now ${tutorial.status}`
        });
        
        setTimeout(() => setStatusMessage(null), 3000);
        
      } catch (error) {
        console.error('Error saving tutorial status to Firestore:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        // Show error message
        setStatusMessage({
          type: 'error',
          title: 'Save Failed',
          message: error.message || 'Could not save tutorial status'
        });
        
        // Revert the change if save fails
        setTutorials(tutorials);
        setTimeout(() => setStatusMessage(null), 4000);
      } finally {
        setSavingTutorialId(null);
      }
    } else {
      console.error('User not authenticated or ID not available');
      setStatusMessage({
        type: 'error',
        title: 'Not Authenticated',
        message: 'Please log in as an instructor'
      });
      setSavingTutorialId(null);
      setTutorials(tutorials);
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  return (
    <div style={{ background: '#ffffff', minHeight: 'calc(100vh - 70px)', padding: '3rem 2rem' }}>
      {/* Status Message Modal */}
      {statusMessage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: statusMessage.type === 'success' ? '#e8f5e9' : '#ffebee',
          border: `2px solid ${statusMessage.type === 'success' ? '#4caf50' : '#f44336'}`,
          borderRadius: '8px',
          padding: '1.5rem',
          maxWidth: '400px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideInBottom 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ fontSize: '1.5rem' }}>
              {statusMessage.type === 'success' ? '✓' : '✕'}
            </div>
            <div>
              <h4 style={{ 
                margin: '0 0 0.5rem 0', 
                color: statusMessage.type === 'success' ? '#2e7d32' : '#c62828',
                fontWeight: 700
              }}>
                {statusMessage.title}
              </h4>
              <p style={{ margin: 0, color: statusMessage.type === 'success' ? '#1b5e20' : '#b71c1c', fontSize: '0.95rem' }}>
                {statusMessage.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideInBottom {
          from {
            transform: translateY(400px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem', display: 'flex', alignItems: 'center', gap: '1rem', paddingBottom: '1.5rem', borderBottom: '2px solid #e8e8e8' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#1a1a1a',
              border: 'none',
              color: 'white',
              padding: '0.6rem 0.8rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 300ms ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#2d2d2d';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#1a1a1a';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1a1a1a', letterSpacing: '-1px', margin: '0' }}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '1rem', color: '#1a1a1a' }}></i>
              Manage Weekly Tutorials
            </h1>
            <p style={{ fontSize: '1rem', color: '#666', margin: '0.5rem 0 0 0' }}>
              Add, release, and manage all your weekly tutorial content
            </p>
          </div>
        </div>

        {/* Add Tutorial Button */}
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowTutorialForm(!showTutorialForm)}
            style={{
              background: '#1a1a1a',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 300ms ease'
            }}
            onMouseOver={(e) => e.target.style.opacity = '0.9'}
            onMouseOut={(e) => e.target.style.opacity = '1'}
          >
            {showTutorialForm ? '✕ Cancel' : '+ Add New Tutorial'}
          </button>
        </div>

        {/* Add Tutorial Form */}
        {showTutorialForm && (
          <div style={{
            background: '#f9f9f9',
            border: '2px solid #e8e8e8',
            borderRadius: '8px',
            padding: '2.5rem',
            marginBottom: '3rem'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.5rem', color: '#1a1a1a' }}>
              Add New Tutorial
            </h2>
            <form onSubmit={handleAddTutorial}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.75rem', color: '#1a1a1a' }}>
                  Tutorial Title
                </label>
                <input
                  type="text"
                  value={tutorialForm.title}
                  onChange={(e) => setTutorialForm({...tutorialForm, title: e.target.value})}
                  placeholder="e.g., Week 1: Foundation Concepts"
                  style={{ 
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid #e8e8e8',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.75rem', color: '#1a1a1a' }}>
                    Duration
                  </label>
                  <input
                    type="text"
                    value={tutorialForm.duration}
                    onChange={(e) => setTutorialForm({...tutorialForm, duration: e.target.value})}
                    placeholder="e.g., 20 mins"
                    style={{ 
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '1px solid #e8e8e8',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.75rem', color: '#1a1a1a' }}>
                    Tutorial URL
                  </label>
                  <input
                    type="url"
                    value={tutorialForm.url}
                    onChange={(e) => setTutorialForm({...tutorialForm, url: e.target.value})}
                    placeholder="https://example.com/tutorial"
                    style={{ 
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '1px solid #e8e8e8',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  style={{
                    background: '#1a1a1a',
                    color: 'white',
                    border: 'none',
                    padding: '0.875rem 1.75rem',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 300ms ease'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.9'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  Add Tutorial
                </button>
                <button
                  type="button"
                  onClick={() => setShowTutorialForm(false)}
                  style={{
                    background: '#e8e8e8',
                    color: '#1a1a1a',
                    border: 'none',
                    padding: '0.875rem 1.75rem',
                    borderRadius: '6px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 300ms ease'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.7'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  Discard
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tutorials List */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          {tutorials.length === 0 ? (
            <div style={{
              background: '#f9f9f9',
              border: '2px dashed #e8e8e8',
              borderRadius: '8px',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '1.1rem', color: '#666', margin: 0 }}>
                No tutorials yet. Create your first tutorial to get started!
              </p>
            </div>
          ) : (
            tutorials.map(tutorial => (
              <div
                key={tutorial.id}
                style={{
                  background: '#d3d3d3',
                  border: '1px solid #c0c0c0',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  transition: 'all 300ms ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
                  <div>
                    <h3 style={{ fontWeight: 700, margin: '0 0 1rem 0', color: '#1a1a1a', fontSize: '1.25rem' }}>
                      {tutorial.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <span style={{ color: '#666', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fas fa-clock"></i>
                        {tutorial.duration}
                      </span>
                      <span style={{
                        background: tutorial.status === 'released' ? '#e8f5e9' : '#ffebee',
                        color: tutorial.status === 'released' ? '#2e7d32' : '#c62828',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: 600
                      }}>
                        {tutorial.status === 'released' ? '● Released' : '● Closed'}
                      </span>
                    </div>
                    <p style={{ color: '#999', margin: '0', wordBreak: 'break-all', fontSize: '0.95rem' }}>
                      <strong>URL:</strong> {tutorial.url}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', minWidth: '150px' }}>
                    <button
                      onClick={() => toggleTutorialStatus(tutorial.id)}
                      disabled={savingTutorialId === tutorial.id}
                      style={{
                        background: savingTutorialId === tutorial.id ? '#dcdcdc' : (tutorial.status === 'released' ? '#a9a9a9' : '#1a1a1a'),
                        color: savingTutorialId === tutorial.id ? '#999' : (tutorial.status === 'released' ? '#1a1a1a' : 'white'),
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        fontWeight: 600,
                        cursor: savingTutorialId === tutorial.id ? 'not-allowed' : 'pointer',
                        fontSize: '1rem',
                        transition: 'all 300ms ease',
                        opacity: savingTutorialId === tutorial.id ? 0.7 : 1
                      }}
                      onMouseOver={(e) => {
                        if (savingTutorialId !== tutorial.id) {
                          e.target.style.opacity = '0.9';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (savingTutorialId !== tutorial.id) {
                          e.target.style.opacity = '1';
                        }
                      }}
                    >
                      {savingTutorialId === tutorial.id ? (
                        <>
                          <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                          Saving...
                        </>
                      ) : (
                        tutorial.status === 'released' ? 'Close Tutorial' : 'Release Tutorial'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
